import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ChevronDown, ChevronRight, ChevronLeft, Clock, DollarSign, Calendar, Users, EuroIcon, AlertTriangle, Copy, User, TrendingUp, TrendingDown, Minus, Activity, Shield, UserCheck } from 'lucide-react';
import AnnualChart from './AnnualChart';
import RevenueDisplay from './RevenueDisplay';

const ClientReport = ({ client, db }) => {
  // Vérification de sécurité pour client null
  if (!client) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Rapport client</h1>
        <div className="text-center p-8">
          <User size={48} className="mx-auto mb-4 text-gray-400 dark:text-gray-500" />
          <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Client non sélectionné</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Veuillez sélectionner un client depuis la liste des bénéficiaires pour accéder au rapport.
          </p>
        </div>
      </div>
    );
  }

  const [contract, setContract] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [employeeStats, setEmployeeStats] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [employeeNames, setEmployeeNames] = useState({});
  const [selectedMonth, setSelectedMonth] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [anomalies, setAnomalies] = useState([]);
  const [previousMonthStats, setPreviousMonthStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    dashboard: true,
    alerts: true,
    hours: true,
    employees: true,
    financial: true,
    anomalies: true,
    details: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch contract
        const contractDoc = await getDoc(doc(db, 'contracts', client.id));
        let contractData = null;
        if (contractDoc.exists()) {
          contractData = contractDoc.data();
          setContract(contractData);
        }

        // Fetch employee names
        const employeesSnapshot = await getDocs(collection(db, 'employees'));
        const employeeNamesData = {};
        employeesSnapshot.forEach(doc => {
          employeeNamesData[doc.id] = `${doc.data().firstName} ${doc.data().lastName}`;
        });
        setEmployeeNames(employeeNamesData);

        // Set initial month display
        setSelectedMonth(currentDate.toLocaleString('default', { month: 'long', year: 'numeric' }));

        // Fetch initial month's data using the fetched contract data
        await fetchMonthData(currentDate, contractData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [client.id, db]);

  const fetchMonthData = async (date, contractData) => {
    try {
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      const startOfNextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);

      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('clientId', '==', client.id),
        where('date', '>=', startOfMonth),
        where('date', '<', startOfNextMonth)
      );

      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      const appointments = appointmentsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const { monthStats, empStats, anomalies } = processAppointments(appointments, startOfMonth, endOfMonth, contractData);
      setMonthlyStats(monthStats);
      setEmployeeStats(empStats);
      setAnomalies(anomalies);

      // Fetch previous month data for comparison
      const prevMonthDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
      const startOfPrevMonth = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth(), 1);
      const endOfPrevMonth = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth() + 1, 0);
      const startOfCurrentMonth = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth() + 1, 1);

      const prevAppointmentsQuery = query(
        collection(db, 'appointments'),
        where('clientId', '==', client.id),
        where('date', '>=', startOfPrevMonth),
        where('date', '<', startOfCurrentMonth)
      );

      const prevAppointmentsSnapshot = await getDocs(prevAppointmentsQuery);
      const prevAppointments = prevAppointmentsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const { monthStats: prevMonthStats } = processAppointments(prevAppointments, startOfPrevMonth, endOfPrevMonth, contractData);
      setPreviousMonthStats(prevMonthStats[0]);

      // Generate alerts based on current data
      const generatedAlerts = generateAlerts(monthStats[0], empStats, contractData, prevMonthStats[0]);
      setAlerts(generatedAlerts);
    } catch (error) {
      console.error("Error fetching month data:", error);
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedMonth(newDate.toLocaleString('default', { month: 'long', year: 'numeric' }));
    fetchMonthData(newDate, contract);
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diff = (end - start) / (1000 * 60); // difference in minutes
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return { hours, minutes };
  };

  const formatDuration = (duration) => {
    const hours = duration.hours;
    const minutes = duration.minutes;
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
  };

  const formatTotalDuration = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h${minutes.toString().padStart(2, '0')}`;
  };

  const durationToHours = (duration) => {
    return duration.hours + duration.minutes / 60;
  };

  // Calculer les métriques avancées
  const calculateMetrics = (currentStats, previousStats, contractData, empStats) => {
    const metrics = {};

    // Taux de réalisation (effectué / prévu)
    const plannedHours = contractData?.hoursPerMonth || 0;
    const actualHours = parseFloat(currentStats?.actual || 0);
    const scheduledHours = parseFloat(currentStats?.scheduled || 0);

    metrics.realizationRate = plannedHours > 0 ? (actualHours / plannedHours) * 100 : 0;
    metrics.fillingRate = plannedHours > 0 ? (scheduledHours / plannedHours) * 100 : 0;

    // Évolution vs mois précédent
    if (previousStats) {
      const prevScheduled = parseFloat(previousStats.scheduled || 0);
      metrics.evolution = prevScheduled > 0 ? ((scheduledHours - prevScheduled) / prevScheduled) * 100 : 0;

      const prevAmount = previousStats.amount || 0;
      const currentAmount = currentStats?.amount || 0;
      metrics.revenueEvolution = prevAmount > 0 ? ((currentAmount - prevAmount) / prevAmount) * 100 : 0;
    } else {
      metrics.evolution = 0;
      metrics.revenueEvolution = 0;
    }

    // Statistiques employés
    if (empStats && Object.keys(empStats).length > 0) {
      const employeeList = Object.entries(empStats);
      const totalHours = employeeList.reduce((sum, [_, stats]) => sum + stats.hours, 0);

      // Employé principal (le plus d'heures)
      const mainEmployee = employeeList.reduce((max, [empId, stats]) =>
        stats.hours > (max.stats?.hours || 0) ? { empId, stats } : max
      , { empId: null, stats: null });

      metrics.mainEmployeeId = mainEmployee.empId;
      metrics.mainEmployeeHours = mainEmployee.stats?.hours || 0;
      metrics.concentrationRate = totalHours > 0 ? (mainEmployee.stats?.hours / totalHours) * 100 : 0;
      metrics.numberOfEmployees = employeeList.length;

      // Moyenne de jours travaillés par employé
      const totalDays = employeeList.reduce((sum, [_, stats]) => sum + stats.daysWorked.size, 0);
      metrics.averageDaysPerEmployee = employeeList.length > 0 ? totalDays / employeeList.length : 0;
    }

    // Écart budgétaire
    const budgetedAmount = plannedHours * (contractData?.hourlyRate || 0);
    const actualAmount = currentStats?.amount || 0;
    metrics.budgetGap = budgetedAmount - actualAmount;
    metrics.budgetGapPercent = budgetedAmount > 0 ? ((actualAmount - budgetedAmount) / budgetedAmount) * 100 : 0;

    return metrics;
  };

  // Score de santé du compte (0-100)
  const calculateHealthScore = (metrics, alerts) => {
    let score = 100;

    // Pénalités basées sur les métriques
    const realizationGap = Math.abs(100 - metrics.realizationRate);
    if (realizationGap > 20) score -= 15;
    else if (realizationGap > 10) score -= 10;
    else if (realizationGap > 5) score -= 5;

    // Pénalité pour forte rotation employés
    if (metrics.numberOfEmployees > 4) score -= 10;
    else if (metrics.numberOfEmployees > 3) score -= 5;

    // Pénalité pour faible concentration (changement fréquent d'employés)
    if (metrics.concentrationRate < 50) score -= 10;
    else if (metrics.concentrationRate < 70) score -= 5;

    // Pénalités pour les alertes
    score -= alerts.length * 5;

    return Math.max(0, Math.min(100, score));
  };

  // Générer les alertes intelligentes
  const generateAlerts = (currentStats, empStats, contractData, previousStats) => {
    const alerts = [];
    const plannedHours = contractData?.hoursPerMonth || 0;
    const scheduledHours = parseFloat(currentStats?.scheduled || 0);
    const actualHours = parseFloat(currentStats?.actual || 0);

    // Alerte : Heures très inférieures au contrat
    if (plannedHours > 0 && scheduledHours < plannedHours * 0.8) {
      alerts.push({
        type: 'warning',
        level: 'medium',
        title: 'Heures programmées inférieures au contrat',
        message: `Les heures programmées (${formatTotalDuration(scheduledHours)}) sont ${Math.round(((plannedHours - scheduledHours) / plannedHours) * 100)}% inférieures aux heures contractuelles (${formatTotalDuration(plannedHours)}).`,
        recommendation: 'Envisagez de programmer des rendez-vous supplémentaires pour atteindre le volume contractuel.'
      });
    }

    // Alerte : Heures très supérieures au contrat
    if (plannedHours > 0 && scheduledHours > plannedHours * 1.2) {
      alerts.push({
        type: 'warning',
        level: 'medium',
        title: 'Heures programmées supérieures au contrat',
        message: `Les heures programmées (${formatTotalDuration(scheduledHours)}) dépassent de ${Math.round(((scheduledHours - plannedHours) / plannedHours) * 100)}% les heures contractuelles (${formatTotalDuration(plannedHours)}).`,
        recommendation: 'Vérifiez si un ajustement du contrat est nécessaire ou réduisez les heures programmées.'
      });
    }

    // Alerte : Rotation employés élevée
    const numberOfEmployees = Object.keys(empStats || {}).length;
    if (numberOfEmployees > 3) {
      alerts.push({
        type: 'info',
        level: 'low',
        title: 'Rotation des employés élevée',
        message: `${numberOfEmployees} employés différents sont intervenus ce mois-ci.`,
        recommendation: 'Une rotation élevée peut affecter la continuité du service. Envisagez de stabiliser l\'équipe.'
      });
    }

    // Alerte : Forte baisse vs mois précédent
    if (previousStats) {
      const prevScheduled = parseFloat(previousStats.scheduled || 0);
      if (prevScheduled > 0 && scheduledHours < prevScheduled * 0.7) {
        const decrease = Math.round(((prevScheduled - scheduledHours) / prevScheduled) * 100);
        alerts.push({
          type: 'warning',
          level: 'high',
          title: 'Forte baisse d\'activité',
          message: `Les heures ont diminué de ${decrease}% par rapport au mois précédent.`,
          recommendation: 'Contactez le bénéficiaire pour comprendre la raison de cette baisse et ajuster le planning si nécessaire.'
        });
      }
    }

    // Alerte : Dépassement budgétaire
    const budgetedAmount = plannedHours * (contractData?.hourlyRate || 0);
    const currentAmount = currentStats?.amount || 0;
    if (budgetedAmount > 0 && currentAmount > budgetedAmount * 1.15) {
      alerts.push({
        type: 'warning',
        level: 'high',
        title: 'Risque de dépassement budgétaire',
        message: `Le montant actuel (${currentAmount.toFixed(2)}€) dépasse le budget prévu (${budgetedAmount.toFixed(2)}€) de ${Math.round(((currentAmount - budgetedAmount) / budgetedAmount) * 100)}%.`,
        recommendation: 'Revoyez les heures programmées ou ajustez le budget contractuel.'
      });
    }

    return alerts;
  };

  const processAppointments = (appointments, startDate, endDate, contractData) => {
    const totalDaysInMonth = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24) + 1;
    const totalHoursInMonth = appointments.reduce((total, appointment) => total + durationToHours(calculateDuration(appointment.startTime, appointment.endTime)), 0);
    const averageHoursPerDay = totalHoursInMonth / totalDaysInMonth;
    const scheduledHours = averageHoursPerDay * totalDaysInMonth;

    // Utiliser les heures par mois du contrat comme heures conseillées
    const plannedHours = contractData ? contractData.hoursPerMonth : 0;
    let actualHours = 0;
    const currentDate = new Date();

    appointments.forEach(appointment => {
      const appointmentDate = new Date(appointment.date.toDate());
      const duration = durationToHours(calculateDuration(appointment.startTime, appointment.endTime));

      if (appointmentDate < currentDate) {
        // This appointment has already passed
        actualHours += duration;
      }
    });

    const monthStats = [{
      name: startDate.toLocaleString('default', { month: 'long', year: 'numeric' }),
      planned: formatTotalDuration(plannedHours),
      scheduled: formatTotalDuration(scheduledHours),
      actual: formatTotalDuration(actualHours),
      amount: 0,
      appointments: []
    }];

    const empStats = {};

    appointments.forEach(appointment => {
      const duration = calculateDuration(appointment.startTime, appointment.endTime);
      const durationInHours = durationToHours(duration);

      if (contractData && contractData.hourlyRate) {
        const appointmentAmount = durationInHours * contractData.hourlyRate;
        monthStats[0].amount += appointmentAmount;
      }

      monthStats[0].appointments.push({
        ...appointment,
        duration: formatDuration(duration),
        durationInHours
      });

      if (!empStats[appointment.employeeId]) {
        empStats[appointment.employeeId] = { hours: 0, daysWorked: new Set() };
      }
      empStats[appointment.employeeId].hours += durationInHours;

      // Ajouter la date du rendez-vous pour compter les jours uniques
      const appointmentDate = new Date(appointment.date.toDate()).toDateString();
      empStats[appointment.employeeId].daysWorked.add(appointmentDate);
    });

    monthStats[0].amount = Number(monthStats[0].amount.toFixed(2));

    const { anomalies } = calculateAnomalies(appointments, startDate, endDate);

    return { monthStats, empStats, anomalies };
  };

  const calculateAnomalies = (appointments, startDate, endDate) => {
    const daysInMonth = {};
    const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1;

    // Initialize all days
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      daysInMonth[d.getDate()] = { count: 0, duration: 0, appointments: [], employeeAppointments: {} };
    }

    // Count appointments and duration for each day
    appointments.forEach(appointment => {
      const date = new Date(appointment.date.toDate());
      const day = date.getDate();
      const duration = durationToHours(calculateDuration(appointment.startTime, appointment.endTime));

      daysInMonth[day].count++;
      daysInMonth[day].duration += duration;
      daysInMonth[day].appointments.push(appointment);

      if (!daysInMonth[day].employeeAppointments[appointment.employeeId]) {
        daysInMonth[day].employeeAppointments[appointment.employeeId] = 0;
      }
      daysInMonth[day].employeeAppointments[appointment.employeeId]++;
    });

    // Calculate average appointments per day per employee
    const employeeAverages = {};
    Object.values(daysInMonth).forEach(day => {
      Object.entries(day.employeeAppointments).forEach(([employeeId, count]) => {
        if (!employeeAverages[employeeId]) {
          employeeAverages[employeeId] = { total: 0, days: 0 };
        }
        employeeAverages[employeeId].total += count;
        employeeAverages[employeeId].days++;
      });
    });

    Object.keys(employeeAverages).forEach(employeeId => {
      employeeAverages[employeeId] = employeeAverages[employeeId].total / employeeAverages[employeeId].days;
    });

    const anomalies = [];

    // Detect anomalies based on employee averages
    Object.entries(daysInMonth).forEach(([day, data]) => {
      Object.entries(data.employeeAppointments).forEach(([employeeId, count]) => {
        const averageForEmployee = employeeAverages[employeeId];
        if (count < averageForEmployee * 0.5) {  // Less than 50% of the average
          anomalies.push({
            type: 'low_appointments_employee',
            day: parseInt(day),
            employeeId,
            count,
            average: averageForEmployee.toFixed(1)
          });
        }
      });

      // Check for duplicate appointments
      if (data.count > 1) {
        const timeSlots = {};
        data.appointments.forEach(appointment => {
          const timeSlot = `${appointment.startTime}-${appointment.endTime}`;
          if (timeSlots[timeSlot]) {
            anomalies.push({
              type: 'duplicate',
              day: parseInt(day),
              timeSlot: timeSlot,
              count: timeSlots[timeSlot] + 1
            });
          } else {
            timeSlots[timeSlot] = 1;
          }
        });
      }
    });

    return { anomalies, employeeAverages };
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  const renderEmployeeHoursPieChart = (employeeStatsData) => {
    const data = Object.entries(employeeStatsData).map(([empId, stats]) => ({
      name: employeeNames[empId] || `Employee ${empId}`,
      value: stats.hours
    }));

    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={true}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const renderAnomalies = () => {
    if (anomalies.length === 0) {
      return <p>Aucune anomalie significative détectée.</p>;
    }

    return (
      <div className="space-y-4">
        {anomalies.map((anomaly, index) => (
          <div key={index} className={`border-l-4 p-4 ${
            anomaly.type === 'low_appointments_employee' 
              ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
              : 'bg-orange-100 border-orange-500 text-orange-700'
          }`} role="alert">
            <div className="flex">
              <div className="py-1">
                <AlertTriangle className="h-6 w-6 text-yellow-500 mr-4" />
              </div>
              <div>
                {anomaly.type === 'low_appointments_employee' ? (
                  <>
                    <p className="font-bold">Nombre de rendez-vous inférieur à la moyenne pour un employé</p>
                    <p>
                      {`Le ${anomaly.day} du mois, l'employé ${employeeNames[anomaly.employeeId] || anomaly.employeeId} a ${anomaly.count} rendez-vous`}
                    </p>
                    <p className="text-sm mt-1">
                      Moyenne habituelle : environ {anomaly.average} rendez-vous par jour pour cet employé
                    </p>
                    <p className="text-sm italic mt-1">
                      Note: Vérifiez si cette baisse est prévue ou s'il s'agit d'une anomalie.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-bold">Rendez-vous en double détectés</p>
                    <p>
                      {`Le ${anomaly.day} du mois, ${anomaly.count} rendez-vous à ${anomaly.timeSlot}`}
                    </p>
                    <p className="text-sm italic mt-1">
                      Vérifiez s'il s'agit d'une erreur de planification.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Fonction pour basculer l'expansion des sections
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Rendu du Dashboard récapitulatif
  const renderDashboard = () => {
    if (!monthlyStats[0]) return null;

    const metrics = calculateMetrics(monthlyStats[0], previousMonthStats, contract, employeeStats);
    const healthScore = calculateHealthScore(metrics, alerts);

    const getScoreColor = (score) => {
      if (score >= 80) return 'text-green-600 dark:text-green-400';
      if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
      return 'text-red-600 dark:text-red-400';
    };

    const getScoreBg = (score) => {
      if (score >= 80) return 'bg-green-100 dark:bg-green-900/20';
      if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/20';
      return 'bg-red-100 dark:bg-red-900/20';
    };

    const getTrendIcon = (value) => {
      if (value > 5) return <TrendingUp size={16} className="text-green-600 dark:text-green-400" />;
      if (value < -5) return <TrendingDown size={16} className="text-red-600 dark:text-red-400" />;
      return <Minus size={16} className="text-gray-600 dark:text-gray-400" />;
    };

    const getTrendColor = (value) => {
      if (value > 0) return 'text-green-600 dark:text-green-400';
      if (value < 0) return 'text-red-600 dark:text-red-400';
      return 'text-gray-600 dark:text-gray-400';
    };

    return (
      <div className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('dashboard')}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Activity className="mr-2" size={24} />
            Vue d'ensemble
          </h2>
          {expandedSections.dashboard ? <ChevronDown size={20} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />}
        </div>

        {expandedSections.dashboard && (
          <div>
            {/* Score de santé */}
            <div className={`${getScoreBg(healthScore)} p-6 rounded-lg shadow-md mb-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Shield className="mr-2" size={20} />
                    Score de santé du compte
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Évaluation globale basée sur les métriques clés
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-5xl font-bold ${getScoreColor(healthScore)}`}>{healthScore}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">/100</p>
                </div>
              </div>
            </div>

            {/* KPI principaux */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Taux de remplissage */}
              <div className="bg-blue-100 dark:bg-blue-900/20 p-4 rounded-lg shadow">
                <h3 className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
                  Taux de remplissage
                </h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {metrics.fillingRate.toFixed(0)}%
                </p>
                <div className="flex items-center mt-2 text-sm">
                  {getTrendIcon(metrics.evolution)}
                  <span className={`ml-1 ${getTrendColor(metrics.evolution)}`}>
                    {metrics.evolution > 0 ? '+' : ''}{metrics.evolution.toFixed(1)}% vs mois précédent
                  </span>
                </div>
              </div>

              {/* Heures programmées */}
              <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-lg shadow">
                <h3 className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
                  Heures programmées
                </h3>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {monthlyStats[0]?.scheduled || '0h00'}
                </p>
                <div className="flex items-center mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar size={14} className="mr-1" />
                  sur {formatTotalDuration(contract?.hoursPerMonth || 0)} conseillées
                </div>
              </div>

              {/* Revenus */}
              <div className="bg-purple-100 dark:bg-purple-900/20 p-4 rounded-lg shadow">
                <h3 className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
                  Revenus du mois
                </h3>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {monthlyStats[0]?.amount?.toFixed(0) || 0}€
                </p>
                <div className="flex items-center mt-2 text-sm">
                  {getTrendIcon(metrics.revenueEvolution)}
                  <span className={`ml-1 ${getTrendColor(metrics.revenueEvolution)}`}>
                    {metrics.revenueEvolution > 0 ? '+' : ''}{metrics.revenueEvolution.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Employés */}
              <div className="bg-orange-100 dark:bg-orange-900/20 p-4 rounded-lg shadow">
                <h3 className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
                  Employés intervenants
                </h3>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {metrics.numberOfEmployees || 0}
                </p>
                <div className="flex items-center mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <UserCheck size={14} className="mr-1" />
                  {metrics.concentrationRate?.toFixed(0) || 0}% employé principal
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de la section Analyse financière
  const renderFinancialAnalysis = () => {
    if (!monthlyStats[0]) return null;

    const metrics = calculateMetrics(monthlyStats[0], previousMonthStats, contract, employeeStats);
    const plannedHours = contract?.hoursPerMonth || 0;
    const hourlyRate = contract?.hourlyRate || 0;
    const budgetedAmount = plannedHours * hourlyRate;
    const currentAmount = monthlyStats[0]?.amount || 0;
    const budgetUsagePercent = budgetedAmount > 0 ? (currentAmount / budgetedAmount) * 100 : 0;

    // Projection annuelle simple
    const currentMonth = new Date().getMonth() + 1;
    const yearlyProjection = budgetedAmount * 12;
    const currentYearSpent = currentAmount; // Simplification

    const getBudgetColor = (percent) => {
      if (percent < 80) return 'bg-green-500';
      if (percent < 100) return 'bg-yellow-500';
      if (percent < 120) return 'bg-orange-500';
      return 'bg-red-500';
    };

    return (
      <div className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('financial')}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <EuroIcon className="mr-2" size={24} />
            Analyse Financière
          </h2>
          {expandedSections.financial ? <ChevronDown size={20} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />}
        </div>

        {expandedSections.financial && (
          <div className="space-y-4">
            {/* Jauge budgétaire */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Budget du mois
              </h3>

              <div className="space-y-4">
                {/* Barre de progression */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {currentAmount.toFixed(2)}€ / {budgetedAmount.toFixed(2)}€
                    </span>
                    <span className={`text-sm font-bold ${budgetUsagePercent > 100 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {budgetUsagePercent.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                    <div
                      className={`h-4 rounded-full transition-all duration-500 ${getBudgetColor(budgetUsagePercent)}`}
                      style={{ width: `${Math.min(budgetUsagePercent, 100)}%` }}
                    ></div>
                  </div>
                  {budgetUsagePercent > 100 && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2 flex items-center">
                      <AlertTriangle size={14} className="mr-1" />
                      Dépassement de {(budgetUsagePercent - 100).toFixed(1)}%
                    </p>
                  )}
                </div>

                {/* Détails financiers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400">Budget mensuel</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {budgetedAmount.toFixed(2)}€
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400">Dépensé ce mois</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {currentAmount.toFixed(2)}€
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400">Écart</p>
                    <p className={`text-lg font-bold ${metrics.budgetGap >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {metrics.budgetGap >= 0 ? '+' : ''}{metrics.budgetGap.toFixed(2)}€
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Projection annuelle */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Projection annuelle
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Budget annuel prévu</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {yearlyProjection.toFixed(0)}€
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatTotalDuration(plannedHours * 12)} × {hourlyRate}€/h
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Évolution mensuelle</p>
                  <div className="flex items-center mt-1">
                    {metrics.revenueEvolution > 0 ? (
                      <TrendingUp className="text-green-600 dark:text-green-400 mr-2" size={24} />
                    ) : metrics.revenueEvolution < 0 ? (
                      <TrendingDown className="text-red-600 dark:text-red-400 mr-2" size={24} />
                    ) : (
                      <Minus className="text-gray-600 dark:text-gray-400 mr-2" size={24} />
                    )}
                    <p className={`text-2xl font-bold ${metrics.revenueEvolution > 0 ? 'text-green-600 dark:text-green-400' : metrics.revenueEvolution < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      {metrics.revenueEvolution > 0 ? '+' : ''}{metrics.revenueEvolution.toFixed(1)}%
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    vs mois précédent
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tarif horaire</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {hourlyRate}€/h
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Taux contractuel
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de la section Alertes
  const renderAlertsSection = () => {
    if (alerts.length === 0 && expandedSections.alerts) {
      return (
        <div className="mb-6">
          <div
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => toggleSection('alerts')}
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <AlertTriangle className="mr-2 text-green-600 dark:text-green-400" size={24} />
              Alertes et Recommandations
              <span className="ml-2 text-sm font-normal text-green-600 dark:text-green-400">
                (Aucune)
              </span>
            </h2>
            {expandedSections.alerts ? <ChevronDown size={20} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />}
          </div>
          {expandedSections.alerts && (
            <div className="bg-green-50 dark:bg-green-900/10 p-6 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-green-700 dark:text-green-300 flex items-center">
                <Shield size={20} className="mr-2" />
                Aucune alerte active. Le compte fonctionne normalement.
              </p>
            </div>
          )}
        </div>
      );
    }

    if (alerts.length === 0) return null;

    const getLevelColor = (level) => {
      if (level === 'high') return 'border-red-500 bg-red-100 dark:bg-red-900/20';
      if (level === 'medium') return 'border-yellow-500 bg-yellow-100 dark:bg-yellow-900/20';
      return 'border-blue-500 bg-blue-100 dark:bg-blue-900/20';
    };

    const getLevelIcon = (level) => {
      if (level === 'high') return 'text-red-600 dark:text-red-400';
      if (level === 'medium') return 'text-yellow-600 dark:text-yellow-400';
      return 'text-blue-600 dark:text-blue-400';
    };

    return (
      <div className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('alerts')}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <AlertTriangle className="mr-2 text-red-600 dark:text-red-400" size={24} />
            Alertes et Recommandations
            <span className="ml-2 px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-sm font-semibold">
              {alerts.length}
            </span>
          </h2>
          {expandedSections.alerts ? <ChevronDown size={20} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />}
        </div>

        {expandedSections.alerts && (
          <div className="space-y-4">
            {alerts.map((alert, index) => (
              <div key={index} className={`border-l-4 p-4 rounded ${getLevelColor(alert.level)}`}>
                <div className="flex">
                  <div className="py-1">
                    <AlertTriangle className={`h-6 w-6 mr-4 ${getLevelIcon(alert.level)}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 dark:text-white">{alert.title}</p>
                    <p className="text-gray-700 dark:text-gray-300 mt-1">{alert.message}</p>
                    {alert.recommendation && (
                      <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Recommandation :</strong> {alert.recommendation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!contract) {
    return <div className="text-gray-900 dark:text-white">Chargement des données...</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Rapport pour {client.firstName} {client.lastName}</h1>


      <AnnualChart client={client} db={db} />

      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => changeMonth(-1)}
          className="p-2 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-lg font-semibold text-gray-900 dark:text-white">{selectedMonth}</span>
        <button
          onClick={() => changeMonth(1)}
          className="p-2 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Dashboard récapitulatif */}
      {renderDashboard()}

      {/* Alertes et recommandations */}
      {renderAlertsSection()}

      {/* Section détails mensuels - anciennes cartes */}
      <div className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('details')}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Clock className="mr-2" size={24} />
            Détails mensuels
          </h2>
          {expandedSections.details ? <ChevronDown size={20} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />}
        </div>

        {expandedSections.details && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-100 dark:bg-blue-900/20 p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center text-gray-900 dark:text-white">
            <Clock className="mr-2 text-blue-600 dark:text-blue-400" size={16} />
            Tarif horaire
          </h3>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{contract.hourlyRate}€</p>
        </div>
        <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center text-gray-900 dark:text-white">
            <Calendar className="mr-2 text-green-600 dark:text-green-400" size={16} />
            Heures conseillées
          </h3>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{monthlyStats[0]?.planned || '0h00'}</p>
        </div>
        <div className="bg-orange-100 dark:bg-orange-900/20 p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center text-gray-900 dark:text-white">
            <Calendar className="mr-2 text-orange-600 dark:text-orange-400" size={16} />
            Heures programmées
          </h3>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{monthlyStats[0]?.scheduled || '0h00'}</p>
        </div>
        <div className="bg-pink-100 dark:bg-pink-900/20 p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center text-gray-900 dark:text-white">
            <Clock className="mr-2 text-pink-600 dark:text-pink-400" size={16} />
            Heures effectuées
          </h3>
          <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">{monthlyStats[0]?.actual || '0h00'}</p>
        </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2 flex items-center text-gray-900 dark:text-white">
          <Users className="mr-2 text-gray-700 dark:text-gray-300" size={20} />
          Heures programmées
        </h3>
        {monthlyStats.map((month, index) => (
          <div key={index} className="mb-2 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => toggleMonth(month.name)}
            >
              {expandedMonths[month.name] ? <ChevronDown size={16} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={16} className="text-gray-700 dark:text-gray-300" />}
              <span className="font-semibold text-gray-900 dark:text-white">{month.name}: {month.scheduled}</span>
            </div>
            {expandedMonths[month.name] && (
              <ul className="ml-6 mt-2 space-y-1">
                {month.appointments.map((app, appIndex) => (
                  <li key={appIndex} className="text-sm text-gray-600 dark:text-gray-400">
                    {new Date(app.date.toDate()).toLocaleDateString()}: {app.startTime} - {app.endTime} ({app.duration})
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2 flex items-center text-gray-900 dark:text-white">
          <AlertTriangle className="mr-2 text-gray-700 dark:text-gray-300" size={20} />
          Anomalies
        </h3>
        <div className="text-gray-900 dark:text-white">
          {renderAnomalies()}
        </div>
      </div>

      <div className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('employees')}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Users className="mr-2" size={24} />
            Statistiques Employés
          </h2>
          {expandedSections.employees ? <ChevronDown size={20} className="text-gray-700 dark:text-gray-300" /> : <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />}
        </div>

        {expandedSections.employees && (
          <>
            {/* Informations globales */}
            {monthlyStats[0] && (() => {
              const metrics = calculateMetrics(monthlyStats[0], previousMonthStats, contract, employeeStats);
              return (
                <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg border border-indigo-200 dark:border-indigo-800">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Employé principal</p>
                      <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                        {metrics.mainEmployeeId ? employeeNames[metrics.mainEmployeeId] : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {metrics.mainEmployeeHours > 0 ? formatTotalDuration(metrics.mainEmployeeHours) : '0h00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Taux de concentration</p>
                      <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                        {metrics.concentrationRate?.toFixed(0) || 0}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Part de l'employé principal
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Nombre d'intervenants</p>
                      <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                        {metrics.numberOfEmployees || 0}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Moyenne: {metrics.averageDaysPerEmployee?.toFixed(1) || 0} jours/employé
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Cartes par employé */}
            <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Détails par employé</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(employeeStats).map(([employeeId, stats]) => {
                const amount = (stats.hours * contract.hourlyRate).toFixed(2);
                const daysWorked = stats.daysWorked.size;
                const totalHours = Object.values(employeeStats).reduce((sum, s) => sum + s.hours, 0);
                const percentage = totalHours > 0 ? (stats.hours / totalHours) * 100 : 0;
                const isMainEmployee = monthlyStats[0] && calculateMetrics(monthlyStats[0], previousMonthStats, contract, employeeStats).mainEmployeeId === employeeId;

                return (
                  <div key={employeeId} className={`${isMainEmployee ? 'bg-indigo-100 dark:bg-indigo-900/30 border-2 border-indigo-400 dark:border-indigo-600' : 'bg-purple-100 dark:bg-purple-900/20'} p-4 rounded-lg shadow relative`}>
                    {isMainEmployee && (
                      <div className="absolute top-2 right-2">
                        <UserCheck size={20} className="text-indigo-600 dark:text-indigo-400" title="Employé principal" />
                      </div>
                    )}
                    <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white pr-8">
                      {employeeNames[employeeId] || `Employé ${employeeId}`}
                    </h3>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {formatTotalDuration(stats.hours)}
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 dark:text-gray-300 flex items-center">
                          <Calendar size={14} className="inline mr-1" />
                          {daysWorked} jour{daysWorked > 1 ? 's' : ''}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {percentage.toFixed(0)}%
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {amount}€
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Analyse financière */}
      {renderFinancialAnalysis()}

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Répartition des heures par employé</h3>
        {renderEmployeeHoursPieChart(employeeStats)}
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Statistiques mensuelles</h3>
        {monthlyStats.map((month, index) => (
          <div key={index} className="mb-6 border-b dark:border-gray-700 pb-4">
            <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow">
              <h5 className="text-md font-semibold mb-2 text-gray-900 dark:text-white">Heures prévues vs effectuées</h5>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[{
                  name: month.name,
                  "Heures prévues": parseFloat(month.planned),
                  "Heures effectuées": parseFloat(month.actual)
                }]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Heures prévues" fill="#4299E1" />
                  <Bar dataKey="Heures effectuées" fill="#48BB78" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>

      <RevenueDisplay monthlyStats={monthlyStats} />
    </div>
  );
};

export default ClientReport;
